import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ComplianceOverview from './components/ComplianceOverview';
import RegulationTracking from './components/RegulationTracking';
import DataRetentionPolicies from './components/DataRetentionPolicies';
import ConsentManagement from './components/ConsentManagement';
import { auditTrailService } from '../../lib/auditTrailService';

const ComplianceMonitoringCenter = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [complianceData, setComplianceData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const { data } = await auditTrailService?.getComplianceMonitoring();
      setComplianceData(data || []);
    } catch (error) {
      console.error('Error loading compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'regulations', label: 'Regulations', icon: 'FileText' },
    { id: 'retention', label: 'Data Retention', icon: 'Database' },
    { id: 'consent', label: 'Consent Management', icon: 'CheckSquare' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                Compliance Monitoring Center
              </h1>
              <p className="text-muted-foreground">
                Regulatory compliance tracking and data governance
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadComplianceData}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              <Button variant="default" size="sm">
                <Icon name="Download" size={16} className="mr-2" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-lg border border-border mb-6">
            <div className="flex items-center gap-2 p-2 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {loading ? (
              <div className="card">
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" size={32} className="animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading compliance data...</span>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && <ComplianceOverview data={complianceData} />}
                {activeTab === 'regulations' && <RegulationTracking data={complianceData} onRefresh={loadComplianceData} />}
                {activeTab === 'retention' && <DataRetentionPolicies />}
                {activeTab === 'consent' && <ConsentManagement />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceMonitoringCenter;